// WHAT HAPPENS AFTER AN ACCOUNT STEP SUCCEEDS (ACC-3a): where the owner goes, and what becomes of this tab's look-around.
//
// THE LOOK-AROUND (ACCOUNTS_PLAN §2.3, `LookAround` in `services/auth.ts`):
//   carriedOver — the look-around was LINKED to the new account: the same uid, so this tab's appeal and everything under
//                 it came along. Nothing to do.
//   leftBehind  — the owner signed in to an account that ALREADY EXISTED (a password, or an Apple ID the app made): the
//                 look-around stays on its old uid. WHEN THE APPEAL THIS TAB HOLDS WAS THE LOOK-AROUND'S AND UNPAID —
//                 the server's own answer, read before the sign-in (`useUnpaidLookAround`), and exactly when `/signin`
//                 shows B7 — B7 is the promise made about it: "We'll check this address again under your account". So
//                 this tab lets the id go and, when it still has the address the owner typed, asks again under the
//                 account, which starts the account's own appeal for it. After a reload the typed address is gone (it is
//                 kept in memory only); the owner then starts from the address box, where `/checkout`'s guard sends a
//                 tab holding no appeal anyway. An appeal that was NOT the look-around's — the account's own, held
//                 across a sign-in that ended under the page (F3) — is kept: signing back in makes it theirs again.
//
// WHERE TO: the page the owner was on when they were sent here (`from`, carried in the router's state — never a URL
// parameter, so nothing outside this app can name it), or `/checkout`, which is where every account step on the way to
// paying leads. A tab holding no appeal is sent from there to `/` by `/checkout`'s own guard.

import { useNavigate } from "react-router-dom";
import type { V2Result } from "@contract";
import type { AccountResult } from "../../services";
import { signedIn } from "../../app/accounts";
import { unitOrNull } from "../../app/handIn";
import { useSession } from "../../app/session";
import { readSnapshot } from "../../app/snapshot";
import { useCall } from "../../app/useCall";

export interface AccountState {
  /** Where the owner was, to go back to after the account step. An in-app path. */
  readonly from?: string;
  /** Why they were sent: `/checkout` refused a look-around (C8). */
  readonly accountRequired?: boolean;
  /** An address to start a form with (`/forgot` after `/signin`, or after an email restored). */
  readonly email?: string;
}

/** The state an account route was navigated to with, read safely: only an in-app path is a `from`. */
export function accountState(state: unknown): AccountState {
  if (typeof state !== "object" || state === null) return {};
  const s = state as Record<string, unknown>;
  const from = typeof s.from === "string" && s.from.startsWith("/") && !s.from.startsWith("//") ? s.from : undefined;
  return {
    from,
    accountRequired: s.accountRequired === true,
    email: typeof s.email === "string" ? s.email : undefined,
  };
}

/**
 * WHETHER THIS TAB HOLDS A LOOK-AROUND'S UNPAID APPEAL: this browser is signed out of any account, it holds an appeal,
 * and the server's snapshot of that appeal, asked as the look-around, is there and unpaid. False while unanswered and on
 * any failure — B7 is a promise, and it is made only when it is known to be true.
 */
export function useUnpaidLookAround(): boolean {
  const session = useSession();
  const answer = useCall(async (): Promise<V2Result<boolean>> => {
    const held = session.appealId;
    const no: V2Result<boolean> = { ok: true, status: 200, response: false };
    if (held === null || signedIn(session.auth)) return no;
    const snapshot = await readSnapshot(session.transport, held);
    if (!snapshot.ok || snapshot.response === null) return no;
    return { ok: true, status: 200, response: !snapshot.response.entitlement.unlocked };
    // Asked on arrival, before any sign-in on the page; not on `revision`, which the sign-in itself bumps.
  }, [session.transport, session.appealId]);
  return answer.status === "ok" && answer.response;
}

/** Finishes an account step: the look-around, then the page. */
export function useFinishAccountStep(): (
  result: Extract<AccountResult, { ok: true }>,
  to: string,
  unpaidLookAround: boolean,
) => Promise<void> {
  const session = useSession();
  const navigate = useNavigate();
  return async (result, to, unpaidLookAround) => {
    if (result.lookAround.kind === "leftBehind" && unpaidLookAround) {
      session.forgetAppeal();
      if (session.typedAddress !== "") {
        // The answer is the next screen's to show: `/checkout` re-asks the address itself, and a refusal there says why.
        await session.resolveAddress({ typedAddress: session.typedAddress, unit: unitOrNull(session.typedUnit) });
      }
    }
    session.refresh();
    navigate(to, { replace: true });
  };
}
